import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  DatePicker,
  Space,
  Row,
  Col,
  message,
  Tooltip,
  Tabs,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  DownloadOutlined,
  QuestionCircleOutlined,
  DownOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

// 游戏数据报表接口
interface GameDataReportItem {
  id: string;
  productType?: string; // 产品类型（按产品类型分类时使用）
  gameProvider?: string; // 游戏厂商（按游戏厂商分类时使用）
  subGame?: string; // 子游戏（按子游戏展示时使用）
  uniquePlayers: number; // 不重复玩家数
  betCount: number; // 投注笔数
  totalBetAmount: number; // 总投注额
  validBetAmount: number; // 有效投注额
  payoutAmount: number; // 派彩金额
  winLoss: number; // 输/赢
  profitRate: number; // 盈利率（%）
  isTotal?: boolean; // 是否为总计行
  children?: GameDataReportItem[]; // 子行（用于展开）
}

// 产品类型列表
const productTypes = ['电子游艺', '真人娱乐', '多人游戏', '体育竞猜', '彩票游戏'];

// 游戏厂商列表
const gameProviders = [
  { name: 'AG游戏', productType: '真人娱乐' },
  { name: 'BBIN游戏', productType: '电子游艺' },
  { name: 'PT游戏', productType: '电子游艺' },
  { name: 'MG游戏', productType: '电子游艺' },
  { name: 'PG游戏', productType: '电子游艺' },
  { name: 'EVO游戏', productType: '真人娱乐' },
  { name: 'PP游戏', productType: '电子游艺' },
  { name: 'CQ9游戏', productType: '电子游艺' },
  { name: 'JDB游戏', productType: '电子游艺' },
  { name: '开元棋牌', productType: '多人游戏' },
];

// 子游戏列表
const subGames = [
  { name: '百家乐', provider: 'AG游戏', productType: '真人娱乐' },
  { name: '龙虎', provider: 'AG游戏', productType: '真人娱乐' },
  { name: '轮盘', provider: 'AG游戏', productType: '真人娱乐' },
  { name: '骰宝', provider: 'AG游戏', productType: '真人娱乐' },
  { name: '大富翁', provider: 'BBIN游戏', productType: '电子游艺' },
  { name: '水果机', provider: 'PT游戏', productType: '电子游艺' },
  { name: '捕鱼达人', provider: 'MG游戏', productType: '电子游艺' },
  { name: '德州扑克', provider: '开元棋牌', productType: '多人游戏' },
  { name: '斗地主', provider: '开元棋牌', productType: '多人游戏' },
];

// 生成随机数字
const randomInt = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;
const randomFloat = (min: number, max: number) => Math.random() * (max - min) + min;

// 格式化数字
const formatNumber = (num: number) => {
  return num.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// 生成按产品类型分类的数据
const generateProductTypeData = (): GameDataReportItem[] => {
  const data: GameDataReportItem[] = [];
  let totalUniquePlayers = 0;
  let totalBetCount = 0;
  let totalBetAmount = 0;
  let totalValidBetAmount = 0;
  let totalPayoutAmount = 0;
  let totalWinLoss = 0;

  productTypes.forEach((productType) => {
    const uniquePlayers = randomInt(3, 30);
    const betCount = randomInt(100, 10000);
    const totalBet = randomFloat(10000, 50000000);
    const validBet = totalBet * randomFloat(0.85, 1.0);
    const winLoss = randomFloat(-500000, 10000000);
    const payoutAmount = validBet + winLoss; // 派彩金额 = 有效投注额 + 输/赢
    const profitRate = validBet > 0 ? (winLoss / validBet) * 100 : 0; // 盈利率 = 输/赢 / 有效投注额 * 100%

    totalUniquePlayers += uniquePlayers;
    totalBetCount += betCount;
    totalBetAmount += totalBet;
    totalValidBetAmount += validBet;
    totalPayoutAmount += payoutAmount;
    totalWinLoss += winLoss;

    // 获取该产品类型下的游戏厂商
    const providers = gameProviders.filter(p => p.productType === productType);
    const children: GameDataReportItem[] = providers.map(provider => {
      const childUniquePlayers = randomInt(1, uniquePlayers);
      const childBetCount = randomInt(10, betCount);
      const childTotalBet = randomFloat(1000, totalBet);
      const childValidBet = childTotalBet * randomFloat(0.85, 1.0);
      const childWinLoss = randomFloat(-100000, totalBet * 0.2);
      const childPayoutAmount = childValidBet + childWinLoss;
      const childProfitRate = childValidBet > 0 ? (childWinLoss / childValidBet) * 100 : 0;

      return {
        id: `${productType}-${provider.name}`,
        productType,
        gameProvider: provider.name,
        uniquePlayers: childUniquePlayers,
        betCount: childBetCount,
        totalBetAmount: childTotalBet,
        validBetAmount: childValidBet,
        payoutAmount: childPayoutAmount,
        winLoss: childWinLoss,
        profitRate: childProfitRate,
      };
    });

    data.push({
      id: productType,
      productType,
      gameProvider: '小计',
      uniquePlayers,
      betCount,
      totalBetAmount: totalBet,
      validBetAmount: validBet,
      payoutAmount,
      winLoss,
      profitRate,
      children,
    });
  });

  // 添加总计行
  const totalProfitRate = totalValidBetAmount > 0 ? (totalWinLoss / totalValidBetAmount) * 100 : 0;
  data.push({
    id: 'total',
    productType: '所有产品类型总计',
    uniquePlayers: totalUniquePlayers,
    betCount: totalBetCount,
    totalBetAmount: totalBetAmount,
    validBetAmount: totalValidBetAmount,
    payoutAmount: totalPayoutAmount,
    winLoss: totalWinLoss,
    profitRate: totalProfitRate,
    isTotal: true,
  });

  return data;
};

// 生成按游戏厂商分类的数据
const generateGameProviderData = (): GameDataReportItem[] => {
  const data: GameDataReportItem[] = [];
  let totalUniquePlayers = 0;
  let totalBetCount = 0;
  let totalBetAmount = 0;
  let totalValidBetAmount = 0;
  let totalPayoutAmount = 0;
  let totalWinLoss = 0;

  // 按产品类型分组
  const providersByType: { [key: string]: typeof gameProviders } = {};
  gameProviders.forEach(provider => {
    if (!providersByType[provider.productType]) {
      providersByType[provider.productType] = [];
    }
    providersByType[provider.productType].push(provider);
  });

  Object.keys(providersByType).forEach(productType => {
    const providers = providersByType[productType];
    providers.forEach(provider => {
      const uniquePlayers = randomInt(2, 25);
      const betCount = randomInt(50, 8000);
      const totalBet = randomFloat(5000, 40000000);
      const validBet = totalBet * randomFloat(0.85, 1.0);
      const winLoss = randomFloat(-400000, 8000000);
      const payoutAmount = validBet + winLoss;
      const profitRate = validBet > 0 ? (winLoss / validBet) * 100 : 0;

      totalUniquePlayers += uniquePlayers;
      totalBetCount += betCount;
      totalBetAmount += totalBet;
      totalValidBetAmount += validBet;
      totalPayoutAmount += payoutAmount;
      totalWinLoss += winLoss;

      // 获取该游戏厂商下的子游戏
      const games = subGames.filter(g => g.provider === provider.name);
      const children: GameDataReportItem[] = games.map(game => {
        const childUniquePlayers = randomInt(1, uniquePlayers);
        const childBetCount = randomInt(5, betCount);
        const childTotalBet = randomFloat(500, totalBet);
        const childValidBet = childTotalBet * randomFloat(0.85, 1.0);
        const childWinLoss = randomFloat(-50000, totalBet * 0.15);
        const childPayoutAmount = childValidBet + childWinLoss;
        const childProfitRate = childValidBet > 0 ? (childWinLoss / childValidBet) * 100 : 0;

        return {
          id: `${provider.name}-${game.name}`,
          productType: provider.productType,
          gameProvider: provider.name,
          subGame: game.name,
          uniquePlayers: childUniquePlayers,
          betCount: childBetCount,
          totalBetAmount: childTotalBet,
          validBetAmount: childValidBet,
          payoutAmount: childPayoutAmount,
          winLoss: childWinLoss,
          profitRate: childProfitRate,
        };
      });

      data.push({
        id: provider.name,
        productType: provider.productType,
        gameProvider: provider.name,
        uniquePlayers,
        betCount,
        totalBetAmount: totalBet,
        validBetAmount: validBet,
        payoutAmount,
        winLoss,
        profitRate,
        children: children.length > 0 ? children : undefined,
      });
    });
  });

  // 添加总计行
  const totalProfitRate = totalValidBetAmount > 0 ? (totalWinLoss / totalValidBetAmount) * 100 : 0;
  data.push({
    id: 'total',
    productType: '所有游戏厂商总计',
    uniquePlayers: totalUniquePlayers,
    betCount: totalBetCount,
    totalBetAmount: totalBetAmount,
    validBetAmount: totalValidBetAmount,
    payoutAmount: totalPayoutAmount,
    winLoss: totalWinLoss,
    profitRate: totalProfitRate,
    isTotal: true,
  });

  return data;
};

// 生成按子游戏展示的数据
const generateSubGameData = (): GameDataReportItem[] => {
  const data: GameDataReportItem[] = [];
  let totalUniquePlayers = 0;
  let totalBetCount = 0;
  let totalBetAmount = 0;
  let totalValidBetAmount = 0;
  let totalPayoutAmount = 0;
  let totalWinLoss = 0;

  subGames.forEach(game => {
    const uniquePlayers = randomInt(1, 20);
    const betCount = randomInt(10, 5000);
    const totalBet = randomFloat(1000, 20000000);
    const validBet = totalBet * randomFloat(0.85, 1.0);
    const winLoss = randomFloat(-200000, 5000000);
    const payoutAmount = validBet + winLoss;
    const profitRate = validBet > 0 ? (winLoss / validBet) * 100 : 0;

    totalUniquePlayers += uniquePlayers;
    totalBetCount += betCount;
    totalBetAmount += totalBet;
    totalValidBetAmount += validBet;
    totalPayoutAmount += payoutAmount;
    totalWinLoss += winLoss;

    data.push({
      id: game.name,
      productType: game.productType,
      gameProvider: game.provider,
      subGame: game.name,
      uniquePlayers,
      betCount,
      totalBetAmount: totalBet,
      validBetAmount: validBet,
      payoutAmount,
      winLoss,
      profitRate,
    });
  });

  // 添加总计行
  const totalProfitRate = totalValidBetAmount > 0 ? (totalWinLoss / totalValidBetAmount) * 100 : 0;
  data.push({
    id: 'total',
    productType: '所有子游戏总计',
    uniquePlayers: totalUniquePlayers,
    betCount: totalBetCount,
    totalBetAmount: totalBetAmount,
    validBetAmount: totalValidBetAmount,
    payoutAmount: totalPayoutAmount,
    winLoss: totalWinLoss,
    profitRate: totalProfitRate,
    isTotal: true,
  });

  return data;
};

const GameDataReport: React.FC = () => {
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs(),
    dayjs(),
  ]);
  const [activeTab, setActiveTab] = useState<string>('product-type');
  const [loading, setLoading] = useState(false);
  const [productTypeData, setProductTypeData] = useState<GameDataReportItem[]>([]);
  const [gameProviderData, setGameProviderData] = useState<GameDataReportItem[]>([]);
  const [subGameData, setSubGameData] = useState<GameDataReportItem[]>([]);

  // 初始化数据
  React.useEffect(() => {
    setProductTypeData(generateProductTypeData());
    setGameProviderData(generateGameProviderData());
    setSubGameData(generateSubGameData());
  }, []);

  // 处理搜索
  const handleSearch = () => {
    setLoading(true);
    setTimeout(() => {
      setProductTypeData(generateProductTypeData());
      setGameProviderData(generateGameProviderData());
      setSubGameData(generateSubGameData());
      setLoading(false);
      message.success('查询成功');
    }, 500);
  };

  // 处理重置
  const handleReset = () => {
    setDateRange([dayjs(), dayjs()]);
    setProductTypeData(generateProductTypeData());
    setGameProviderData(generateGameProviderData());
    setSubGameData(generateSubGameData());
    message.info('已重置');
  };

  // 处理导出Excel
  const handleExportExcel = () => {
    message.success('Excel导出功能开发中...');
  };

  // 处理点击不重复玩家数
  const handleUniquePlayersClick = (record: GameDataReportItem) => {
    message.info(`查看 ${record.productType || record.gameProvider || record.subGame} 的玩家明细（功能开发中）`);
  };

  // 按产品类型分类的表格列
  const productTypeColumns: ColumnsType<GameDataReportItem> = [
    {
      title: (
        <span>
          产品类
          <Tooltip title="游戏产品的分类类型，如电子游艺、真人娱乐、多人游戏等">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'productType',
      key: 'productType',
      width: 150,
      render: (text: string, record: GameDataReportItem) => {
        if (record.isTotal) {
          return <strong>{text}</strong>;
        }
        return text;
      },
    },
    {
      title: (
        <span>
          游戏商
          <Tooltip title="提供游戏服务的厂商名称，如AG游戏、BBIN游戏等">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'gameProvider',
      key: 'gameProvider',
      width: 150,
      render: (text: string, record: GameDataReportItem) => {
        if (record.isTotal) {
          return null;
        }
        if (record.children && record.children.length > 0) {
          return (
            <Space>
              {text}
              <DownOutlined style={{ fontSize: 12, color: '#999' }} />
            </Space>
          );
        }
        return text;
      },
    },
    {
      title: (
        <span>
          不重复玩家数
          <Tooltip title="统计时间范围内参与游戏的唯一玩家数量">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'uniquePlayers',
      key: 'uniquePlayers',
      width: 130,
      align: 'right',
      render: (num: number, record: GameDataReportItem) => {
        if (record.isTotal) {
          return <strong>{num}</strong>;
        }
        if (num > 0) {
          return (
            <span
              style={{ color: '#1890ff', cursor: 'pointer' }}
              onClick={() => handleUniquePlayersClick(record)}
            >
              {num}
            </span>
          );
        }
        return num;
      },
    },
    {
      title: (
        <span>
          投注笔数
          <Tooltip title="统计时间范围内的投注交易总笔数">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'betCount',
      key: 'betCount',
      width: 120,
      align: 'right',
      render: (num: number, record: GameDataReportItem) => {
        if (record.isTotal) {
          return <strong>{num.toLocaleString()}</strong>;
        }
        return num.toLocaleString();
      },
    },
    {
      title: (
        <span>
          总投注额
          <Tooltip title="统计时间范围内所有投注金额的总和">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'totalBetAmount',
      key: 'totalBetAmount',
      width: 150,
      align: 'right',
      render: (num: number, record: GameDataReportItem) => {
        if (record.isTotal) {
          return <strong>{formatNumber(num)}</strong>;
        }
        return formatNumber(num);
      },
    },
    {
      title: (
        <span>
          有效投注额
          <Tooltip title="统计时间范围内符合有效投注条件的投注金额总和">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'validBetAmount',
      key: 'validBetAmount',
      width: 150,
      align: 'right',
      render: (num: number, record: GameDataReportItem) => {
        if (record.isTotal) {
          return <strong>{formatNumber(num)}</strong>;
        }
        return formatNumber(num);
      },
    },
    {
      title: (
        <span>
          派彩金额
          <Tooltip title="实际派发给玩家的金额，等于有效投注额加上输赢金额">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'payoutAmount',
      key: 'payoutAmount',
      width: 150,
      align: 'right',
      render: (num: number, record: GameDataReportItem) => {
        if (record.isTotal) {
          return <strong>{formatNumber(num)}</strong>;
        }
        return formatNumber(num);
      },
    },
    {
      title: (
        <span>
          输/赢
          <Tooltip title="平台盈亏金额，正数表示平台盈利（玩家输），负数表示平台亏损（玩家赢）">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'winLoss',
      key: 'winLoss',
      width: 150,
      align: 'right',
      render: (num: number, record: GameDataReportItem) => {
        const formatted = formatNumber(num);
        const color = num >= 0 ? '#ff4d4f' : '#1890ff';
        if (record.isTotal) {
          return <strong style={{ color }}>{formatted}</strong>;
        }
        return <span style={{ color }}>{formatted}</span>;
      },
    },
    {
      title: (
        <span>
          盈利率
          <Tooltip title="平台盈利率，计算公式为（输赢金额/有效投注额）×100%，正数表示盈利，负数表示亏损">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'profitRate',
      key: 'profitRate',
      width: 120,
      align: 'right',
      render: (num: number, record: GameDataReportItem) => {
        const formatted = `${num >= 0 ? '+' : ''}${num.toFixed(2)}%`;
        const color = num >= 0 ? '#ff4d4f' : '#1890ff';
        if (record.isTotal) {
          return <strong style={{ color }}>{formatted}</strong>;
        }
        return <span style={{ color }}>{formatted}</span>;
      },
    },
  ];

  // 按游戏厂商分类的表格列
  const gameProviderColumns: ColumnsType<GameDataReportItem> = [
    {
      title: (
        <span>
          产品类
          <Tooltip title="游戏产品的分类类型，如电子游艺、真人娱乐、多人游戏等">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'productType',
      key: 'productType',
      width: 150,
      render: (text: string, record: GameDataReportItem) => {
        if (record.isTotal) {
          return <strong>{text}</strong>;
        }
        return text;
      },
    },
    {
      title: (
        <span>
          游戏商
          <Tooltip title="提供游戏服务的厂商名称，如AG游戏、BBIN游戏等">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'gameProvider',
      key: 'gameProvider',
      width: 150,
      render: (text: string, record: GameDataReportItem) => {
        if (record.isTotal) {
          return null;
        }
        if (record.children && record.children.length > 0) {
          return (
            <Space>
              {text}
              <DownOutlined style={{ fontSize: 12, color: '#999' }} />
            </Space>
          );
        }
        return text;
      },
    },
    {
      title: (
        <span>
          不重复玩家数
          <Tooltip title="统计时间范围内参与游戏的唯一玩家数量">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'uniquePlayers',
      key: 'uniquePlayers',
      width: 130,
      align: 'right',
      render: (num: number, record: GameDataReportItem) => {
        if (record.isTotal) {
          return <strong>{num}</strong>;
        }
        if (num > 0) {
          return (
            <span
              style={{ color: '#1890ff', cursor: 'pointer' }}
              onClick={() => handleUniquePlayersClick(record)}
            >
              {num}
            </span>
          );
        }
        return num;
      },
    },
    {
      title: (
        <span>
          投注笔数
          <Tooltip title="统计时间范围内的投注交易总笔数">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'betCount',
      key: 'betCount',
      width: 120,
      align: 'right',
      render: (num: number, record: GameDataReportItem) => {
        if (record.isTotal) {
          return <strong>{num.toLocaleString()}</strong>;
        }
        return num.toLocaleString();
      },
    },
    {
      title: (
        <span>
          总投注额
          <Tooltip title="统计时间范围内所有投注金额的总和">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'totalBetAmount',
      key: 'totalBetAmount',
      width: 150,
      align: 'right',
      render: (num: number, record: GameDataReportItem) => {
        if (record.isTotal) {
          return <strong>{formatNumber(num)}</strong>;
        }
        return formatNumber(num);
      },
    },
    {
      title: (
        <span>
          有效投注额
          <Tooltip title="统计时间范围内符合有效投注条件的投注金额总和">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'validBetAmount',
      key: 'validBetAmount',
      width: 150,
      align: 'right',
      render: (num: number, record: GameDataReportItem) => {
        if (record.isTotal) {
          return <strong>{formatNumber(num)}</strong>;
        }
        return formatNumber(num);
      },
    },
    {
      title: (
        <span>
          派彩金额
          <Tooltip title="实际派发给玩家的金额，等于有效投注额加上输赢金额">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'payoutAmount',
      key: 'payoutAmount',
      width: 150,
      align: 'right',
      render: (num: number, record: GameDataReportItem) => {
        if (record.isTotal) {
          return <strong>{formatNumber(num)}</strong>;
        }
        return formatNumber(num);
      },
    },
    {
      title: (
        <span>
          输/赢
          <Tooltip title="平台盈亏金额，正数表示平台盈利（玩家输），负数表示平台亏损（玩家赢）">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'winLoss',
      key: 'winLoss',
      width: 150,
      align: 'right',
      render: (num: number, record: GameDataReportItem) => {
        const formatted = formatNumber(num);
        const color = num >= 0 ? '#ff4d4f' : '#1890ff';
        if (record.isTotal) {
          return <strong style={{ color }}>{formatted}</strong>;
        }
        return <span style={{ color }}>{formatted}</span>;
      },
    },
    {
      title: (
        <span>
          盈利率
          <Tooltip title="平台盈利率，计算公式为（输赢金额/有效投注额）×100%，正数表示盈利，负数表示亏损">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'profitRate',
      key: 'profitRate',
      width: 120,
      align: 'right',
      render: (num: number, record: GameDataReportItem) => {
        const formatted = `${num >= 0 ? '+' : ''}${num.toFixed(2)}%`;
        const color = num >= 0 ? '#ff4d4f' : '#1890ff';
        if (record.isTotal) {
          return <strong style={{ color }}>{formatted}</strong>;
        }
        return <span style={{ color }}>{formatted}</span>;
      },
    },
  ];

  // 按子游戏展示的表格列
  const subGameColumns: ColumnsType<GameDataReportItem> = [
    {
      title: (
        <span>
          产品类
          <Tooltip title="游戏产品的分类类型，如电子游艺、真人娱乐、多人游戏等">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'productType',
      key: 'productType',
      width: 150,
      render: (text: string, record: GameDataReportItem) => {
        if (record.isTotal) {
          return <strong>{text}</strong>;
        }
        return text;
      },
    },
    {
      title: (
        <span>
          游戏商
          <Tooltip title="提供游戏服务的厂商名称，如AG游戏、BBIN游戏等">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'gameProvider',
      key: 'gameProvider',
      width: 150,
      render: (text: string, record: GameDataReportItem) => {
        if (record.isTotal) {
          return null;
        }
        return text;
      },
    },
    {
      title: (
        <span>
          子游戏
          <Tooltip title="具体的游戏名称，如百家乐、龙虎、大富翁等">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'subGame',
      key: 'subGame',
      width: 150,
      render: (text: string, record: GameDataReportItem) => {
        if (record.isTotal) {
          return <strong>{text}</strong>;
        }
        return text;
      },
    },
    {
      title: (
        <span>
          不重复玩家数
          <Tooltip title="统计时间范围内参与游戏的唯一玩家数量">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'uniquePlayers',
      key: 'uniquePlayers',
      width: 130,
      align: 'right',
      render: (num: number, record: GameDataReportItem) => {
        if (record.isTotal) {
          return <strong>{num}</strong>;
        }
        if (num > 0) {
          return (
            <span
              style={{ color: '#1890ff', cursor: 'pointer' }}
              onClick={() => handleUniquePlayersClick(record)}
            >
              {num}
            </span>
          );
        }
        return num;
      },
    },
    {
      title: (
        <span>
          投注笔数
          <Tooltip title="统计时间范围内的投注交易总笔数">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'betCount',
      key: 'betCount',
      width: 120,
      align: 'right',
      render: (num: number, record: GameDataReportItem) => {
        if (record.isTotal) {
          return <strong>{num.toLocaleString()}</strong>;
        }
        return num.toLocaleString();
      },
    },
    {
      title: (
        <span>
          总投注额
          <Tooltip title="统计时间范围内所有投注金额的总和">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'totalBetAmount',
      key: 'totalBetAmount',
      width: 150,
      align: 'right',
      render: (num: number, record: GameDataReportItem) => {
        if (record.isTotal) {
          return <strong>{formatNumber(num)}</strong>;
        }
        return formatNumber(num);
      },
    },
    {
      title: (
        <span>
          有效投注额
          <Tooltip title="统计时间范围内符合有效投注条件的投注金额总和">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'validBetAmount',
      key: 'validBetAmount',
      width: 150,
      align: 'right',
      render: (num: number, record: GameDataReportItem) => {
        if (record.isTotal) {
          return <strong>{formatNumber(num)}</strong>;
        }
        return formatNumber(num);
      },
    },
    {
      title: (
        <span>
          派彩金额
          <Tooltip title="实际派发给玩家的金额，等于有效投注额加上输赢金额">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'payoutAmount',
      key: 'payoutAmount',
      width: 150,
      align: 'right',
      render: (num: number, record: GameDataReportItem) => {
        if (record.isTotal) {
          return <strong>{formatNumber(num)}</strong>;
        }
        return formatNumber(num);
      },
    },
    {
      title: (
        <span>
          输/赢
          <Tooltip title="平台盈亏金额，正数表示平台盈利（玩家输），负数表示平台亏损（玩家赢）">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'winLoss',
      key: 'winLoss',
      width: 150,
      align: 'right',
      render: (num: number, record: GameDataReportItem) => {
        const formatted = formatNumber(num);
        const color = num >= 0 ? '#ff4d4f' : '#1890ff';
        if (record.isTotal) {
          return <strong style={{ color }}>{formatted}</strong>;
        }
        return <span style={{ color }}>{formatted}</span>;
      },
    },
    {
      title: (
        <span>
          盈利率
          <Tooltip title="平台盈利率，计算公式为（输赢金额/有效投注额）×100%，正数表示盈利，负数表示亏损">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'profitRate',
      key: 'profitRate',
      width: 120,
      align: 'right',
      render: (num: number, record: GameDataReportItem) => {
        const formatted = `${num >= 0 ? '+' : ''}${num.toFixed(2)}%`;
        const color = num >= 0 ? '#ff4d4f' : '#1890ff';
        if (record.isTotal) {
          return <strong style={{ color }}>{formatted}</strong>;
        }
        return <span style={{ color }}>{formatted}</span>;
      },
    },
  ];

  // 获取当前表格数据
  const getCurrentData = () => {
    switch (activeTab) {
      case 'product-type':
        return productTypeData;
      case 'game-provider':
        return gameProviderData;
      case 'sub-game':
        return subGameData;
      default:
        return [];
    }
  };

  // 获取当前表格列
  const getCurrentColumns = () => {
    switch (activeTab) {
      case 'product-type':
        return productTypeColumns;
      case 'game-provider':
        return gameProviderColumns;
      case 'sub-game':
        return subGameColumns;
      default:
        return [];
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <h2 style={{ margin: 0 }}>
            游戏数据报表 {dateRange[0].format('YYYY-MM-DD')} - {dateRange[1].format('YYYY-MM-DD')}
          </h2>
        </div>

        {/* 查询条件 */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Space>
              <span>时间范围：</span>
              <RangePicker
                value={dateRange}
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    setDateRange([dates[0], dates[1]]);
                  }
                }}
                format="YYYY-MM-DD"
              />
            </Space>
          </Col>
          <Col span={16}>
            <Space>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
                loading={loading}
              >
                查询
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                重置
              </Button>
              <Button icon={<DownloadOutlined />} onClick={handleExportExcel}>
                导出Excel
              </Button>
            </Space>
          </Col>
        </Row>

        {/* 标签页 */}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          type="card"
        >
          <TabPane tab="依产品类型分类" key="product-type">
            <Table
              columns={getCurrentColumns()}
              dataSource={getCurrentData()}
              loading={loading}
              rowKey="id"
              pagination={false}
              size="middle"
              rowClassName={(record) => {
                if (record.isTotal) {
                  return 'total-row';
                }
                return '';
              }}
              expandable={{
                expandRowByClick: false,
                expandIcon: ({ expanded, onExpand, record }) => {
                  if (record.isTotal || !record.children || record.children.length === 0) {
                    return <span style={{ width: 24, display: 'inline-block' }} />;
                  }
                  return (
                    <DownOutlined
                      style={{
                        transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s',
                        cursor: 'pointer',
                        fontSize: 12,
                        color: '#999',
                      }}
                      onClick={(e) => onExpand(record, e)}
                    />
                  );
                },
              }}
            />
          </TabPane>
          <TabPane tab="依游戏厂商分类" key="game-provider">
            <Table
              columns={getCurrentColumns()}
              dataSource={getCurrentData()}
              loading={loading}
              rowKey="id"
              pagination={false}
              size="middle"
              rowClassName={(record) => {
                if (record.isTotal) {
                  return 'total-row';
                }
                return '';
              }}
              expandable={{
                expandRowByClick: false,
                expandIcon: ({ expanded, onExpand, record }) => {
                  if (record.isTotal || !record.children || record.children.length === 0) {
                    return <span style={{ width: 24, display: 'inline-block' }} />;
                  }
                  return (
                    <DownOutlined
                      style={{
                        transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s',
                        cursor: 'pointer',
                        fontSize: 12,
                        color: '#999',
                      }}
                      onClick={(e) => onExpand(record, e)}
                    />
                  );
                },
              }}
            />
          </TabPane>
          <TabPane tab="依子游戏展示" key="sub-game">
            <Table
              columns={getCurrentColumns()}
              dataSource={getCurrentData()}
              loading={loading}
              rowKey="id"
              pagination={false}
              size="middle"
              rowClassName={(record) => {
                if (record.isTotal) {
                  return 'total-row';
                }
                return '';
              }}
            />
          </TabPane>
        </Tabs>
      </Card>

      <style>{`
        .total-row {
          background-color: #fafafa;
          font-weight: bold;
        }
        .ant-table-tbody > tr.total-row:hover > td {
          background-color: #f0f0f0 !important;
        }
      `}</style>
    </div>
  );
};

export default GameDataReport;

